import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  Calendar, Users, Clock, Plus, RefreshCw, Edit, Trash2,
  Play, Pause, CheckCircle, AlertCircle, BookOpen, UserPlus, 
  X, Search, ArrowRight, UserMinus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CenterBatchesPage = () => {
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({
    name: '',
    course: '',
    start_date: '',
    end_date: '',
    timing: '',
    start_time: '',
    end_time: '',
    max_students: 30
  });
  const [courses, setCourses] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch courses for dropdown
  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`);
      setCourses(res.data || []);
    } catch (error) {
      console.error('Could not fetch courses:', error);
    }
  };

  useEffect(() => {
    fetchBatches();
    fetchCourses();
  }, []);

  // Auto-update timing when start_time or end_time changes
  const updateTiming = (startTime, endTime) => {
    if (startTime && endTime) {
      const formatTime = (time24) => {
        const [hours, minutes] = time24.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        return `${hour12}:${minutes} ${ampm}`;
      };
      const timing = `${formatTime(startTime)} - ${formatTime(endTime)}`;
      setBatchForm(prev => ({ ...prev, timing }));
    }
  };

  const handleStartTimeChange = (e) => {
    const startTime = e.target.value;
    setBatchForm(prev => ({ ...prev, start_time: startTime }));
    updateTiming(startTime, batchForm.end_time);
  };

  const handleEndTimeChange = (e) => {
    const endTime = e.target.value;
    setBatchForm(prev => ({ ...prev, end_time: endTime }));
    updateTiming(batchForm.start_time, endTime);
  };

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/center/batches`, { headers: getAuthHeaders() });
      setBatches(response.data || []);
    } catch (error) {
      console.error('Fetch batches error:', error);
      toast.error('Failed to load batches');
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBatch = async (e) => {
    e.preventDefault();
    try {
      // Send proper payload matching backend BatchCreate model
      const payload = {
        batch_name: batchForm.name,
        name: batchForm.name,
        course_name: batchForm.course,
        course: batchForm.course,
        start_date: batchForm.start_date,
        end_date: batchForm.end_date || null,
        timing: batchForm.timing,
        max_students: batchForm.max_students || 30
      };
      
      await axios.post(`${API}/center/batches`, payload, { headers: getAuthHeaders() });
      toast.success('Batch created successfully / बैच बना दी गई');
      setAddDialogOpen(false);
      setBatchForm({ name: '', course: '', start_date: '', end_date: '', timing: '', start_time: '', end_time: '', max_students: 30 });
      fetchBatches();
    } catch (error) {
      console.error('Create batch error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create batch');
    }
  };

  // Edit batch
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);

  const handleEditBatch = (batch) => {
    setEditingBatch({
      ...batch,
      name: batch.batch_name || batch.name,
      course: batch.course_name || batch.course
    });
    setEditDialogOpen(true);
  };

  const handleUpdateBatch = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        batch_name: editingBatch.name,
        name: editingBatch.name,
        course_name: editingBatch.course,
        course: editingBatch.course,
        start_date: editingBatch.start_date,
        end_date: editingBatch.end_date || null,
        timing: editingBatch.timing,
        max_students: editingBatch.max_students || 30
      };
      
      await axios.put(`${API}/center/batches/${editingBatch.id}`, payload, { headers: getAuthHeaders() });
      toast.success('Batch updated successfully / बैच अपडेट हो गई');
      setEditDialogOpen(false);
      setEditingBatch(null);
      fetchBatches();
    } catch (error) {
      console.error('Update batch error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update batch');
    }
  };

  // View Students
  const [viewStudentsOpen, setViewStudentsOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchStudents, setBatchStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const handleViewStudents = async (batch) => {
    setSelectedBatch(batch);
    setViewStudentsOpen(true);
    setLoadingStudents(true);
    
    try {
      const response = await axios.get(`${API}/center/batches/${batch.id}`, { headers: getAuthHeaders() });
      setBatchStudents(response.data?.student_details || []);
    } catch (error) {
      console.error('Fetch students error:', error);
      setBatchStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Add Students to Batch
  const [addStudentsOpen, setAddStudentsOpen] = useState(false);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');

  const handleOpenAddStudents = async (batch) => {
    setSelectedBatch(batch);
    setAddStudentsOpen(true);
    setSelectedStudents([]);
    setStudentSearch('');
    setLoadingAvailable(true);
    
    try {
      const response = await axios.get(`${API}/center/batches/${batch.id}/available-students`, { headers: getAuthHeaders() });
      setAvailableStudents(response.data?.available_students || []);
    } catch (error) {
      console.error('Fetch available students error:', error);
      // Fallback: fetch all confirmed students
      try {
        const allStudents = await axios.get(`${API}/center/students?status=confirmed`, { headers: getAuthHeaders() });
        setAvailableStudents(allStudents.data || []);
      } catch {
        setAvailableStudents([]);
      }
    } finally {
      setLoadingAvailable(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleAddStudentsToBatch = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    try {
      await axios.post(
        `${API}/center/batches/${selectedBatch.id}/students`,
        { student_ids: selectedStudents },
        { headers: getAuthHeaders() }
      );
      toast.success(`${selectedStudents.length} student(s) added to batch! / छात्र जोड़े गए!`);
      setAddStudentsOpen(false);
      setSelectedStudents([]);
      fetchBatches();
    } catch (error) {
      console.error('Add students error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add students');
    }
  };

  // Remove Student from Batch
  const handleRemoveStudent = async (studentId) => {
    if (!selectedBatch) return;
    
    try {
      await axios.delete(
        `${API}/center/batches/${selectedBatch.id}/students/${studentId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Student removed from batch / छात्र हटाया गया');
      // Refresh the student list
      handleViewStudents(selectedBatch);
      fetchBatches();
    } catch (error) {
      console.error('Remove student error:', error);
      toast.error('Failed to remove student');
    }
  };

  // Move Student to Another Batch
  const [moveStudentOpen, setMoveStudentOpen] = useState(false);
  const [studentToMove, setStudentToMove] = useState(null);
  const [targetBatchId, setTargetBatchId] = useState('');

  const handleOpenMoveStudent = (student) => {
    setStudentToMove(student);
    setTargetBatchId('');
    setMoveStudentOpen(true);
  };

  const handleMoveStudent = async () => {
    if (!targetBatchId || !studentToMove || !selectedBatch) {
      toast.error('Please select target batch');
      return;
    }
    
    try {
      // Remove from current batch
      await axios.delete(
        `${API}/center/batches/${selectedBatch.id}/students/${studentToMove.id}`,
        { headers: getAuthHeaders() }
      );
      
      // Add to new batch
      await axios.post(
        `${API}/center/batches/${targetBatchId}/students`,
        { student_ids: [studentToMove.id] },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Student moved to new batch! / छात्र नई बैच में भेजा गया!');
      setMoveStudentOpen(false);
      setStudentToMove(null);
      handleViewStudents(selectedBatch);
      fetchBatches();
    } catch (error) {
      console.error('Move student error:', error);
      toast.error('Failed to move student');
    }
  };

  // Filter available students by search
  const filteredAvailableStudents = availableStudents.filter(student => 
    student.name?.toLowerCase().includes(studentSearch.toLowerCase()) ||
    student.mobile?.includes(studentSearch) ||
    student.email?.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'full': return 'bg-amber-100 text-amber-700';
      case 'upcoming': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'active': return <Play className="w-3 h-3" />;
      case 'full': return <AlertCircle className="w-3 h-3" />;
      case 'upcoming': return <Clock className="w-3 h-3" />;
      case 'completed': return <CheckCircle className="w-3 h-3" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="center-batches-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Batch Management</h1>
          <p className="text-slate-500 mt-1">{batches.length} batches running</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchBatches} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 gap-2">
            <Plus className="w-4 h-4" />
            Create Batch
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Play className="w-8 h-8 text-emerald-200" />
            <div>
              <p className="text-emerald-100 text-sm">Active</p>
              <p className="text-2xl font-bold">{batches.filter(b => b.status === 'active').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-blue-200" />
            <div>
              <p className="text-blue-100 text-sm">Upcoming</p>
              <p className="text-2xl font-bold">{batches.filter(b => b.status === 'upcoming').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-8 h-8 text-amber-200" />
            <div>
              <p className="text-amber-100 text-sm">Full</p>
              <p className="text-2xl font-bold">{batches.filter(b => b.status === 'full').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-200" />
            <div>
              <p className="text-purple-100 text-sm">Total Students</p>
              <p className="text-2xl font-bold">{batches.reduce((sum, b) => sum + (b.students_count || b.students?.length || 0), 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Batch Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {batches.map((batch) => (
          <motion.div
            key={batch.id}
            whileHover={{ y: -5 }}
            className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all"
          >
            <div className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900 text-lg">{batch.batch_name || batch.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <BookOpen className="w-4 h-4 text-purple-500" />
                    <span className="text-sm text-slate-600">{batch.course_name || batch.course}</span>
                  </div>
                </div>
                <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(batch.status)}`}>
                  {getStatusIcon(batch.status)}
                  {batch.status}
                </span>
              </div>
              
              <div className="space-y-2 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{batch.start_date?.split('T')[0]} - {batch.end_date?.split('T')[0] || 'Ongoing'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{batch.timing}</span>
                </div>
              </div>
              
              {/* Progress */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-500">Students</span>
                  <span className="font-medium text-slate-900">
                    {batch.students_count || batch.students?.length || 0}/{batch.max_students || 30}
                  </span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      (batch.students_count || 0) >= (batch.max_students || 30) ? 'bg-amber-500' :
                      (batch.students_count || 0) >= (batch.max_students || 30) * 0.8 ? 'bg-emerald-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${((batch.students_count || batch.students?.length || 0) / (batch.max_students || 30)) * 100}%` }}
                  />
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 gap-1"
                  onClick={() => handleViewStudents(batch)}
                >
                  <Users className="w-4 h-4" />
                  View
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                  onClick={() => handleOpenAddStudents(batch)}
                >
                  <UserPlus className="w-4 h-4" />
                  Add
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleEditBatch(batch)}
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Add Batch Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-600" />
              Create New Batch
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBatch} className="space-y-4">
            <div>
              <Label>Batch Name / बैच का नाम</Label>
              <Input
                value={batchForm.name}
                onChange={(e) => setBatchForm({...batchForm, name: e.target.value})}
                placeholder="e.g., AutoCAD Batch A"
                required
              />
            </div>
            <div>
              <Label>Course / कोर्स</Label>
              <select
                value={batchForm.course}
                onChange={(e) => setBatchForm({...batchForm, course: e.target.value})}
                className="w-full h-10 px-3 border rounded-lg bg-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                required
              >
                <option value="">Select Course / कोर्स चुनें</option>
                {courses && courses.length > 0 ? courses.map((course) => (
                  <option key={course.id} value={course.name}>
                    {course.name}
                  </option>
                )) : <option disabled>Loading courses...</option>}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date / शुरू तारीख</Label>
                <Input
                  type="date"
                  value={batchForm.start_date}
                  onChange={(e) => setBatchForm({...batchForm, start_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>End Date / समाप्ति तारीख</Label>
                <Input
                  type="date"
                  value={batchForm.end_date}
                  onChange={(e) => setBatchForm({...batchForm, end_date: e.target.value})}
                  required
                />
              </div>
            </div>
            
            {/* Time Selection with Auto-Update */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time / शुरू समय</Label>
                <Input
                  type="time"
                  value={batchForm.start_time}
                  onChange={handleStartTimeChange}
                  className="cursor-pointer"
                  required
                />
              </div>
              <div>
                <Label>End Time / समाप्ति समय</Label>
                <Input
                  type="time"
                  value={batchForm.end_time}
                  onChange={handleEndTimeChange}
                  className="cursor-pointer"
                  required
                />
              </div>
            </div>
            
            {/* Auto-Generated Timing Display */}
            {batchForm.timing && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-purple-700">
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">Timing / समय:</span>
                  <span className="text-purple-900 font-semibold">{batchForm.timing}</span>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Students / अधिकतम छात्र</Label>
                <Input
                  type="number"
                  value={batchForm.max_students}
                  onChange={(e) => setBatchForm({...batchForm, max_students: parseInt(e.target.value) || 30})}
                  min="1"
                  max="100"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-gradient-to-r from-purple-600 to-blue-600">
                <Plus className="w-4 h-4 mr-2" />
                Create Batch / बैच बनाएं
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Batch Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-purple-600" />
              Edit Batch / बैच एडिट करें
            </DialogTitle>
          </DialogHeader>
          {editingBatch && (
            <form onSubmit={handleUpdateBatch} className="space-y-4">
              <div>
                <Label>Batch Name / बैच का नाम</Label>
                <Input
                  value={editingBatch.name || ''}
                  onChange={(e) => setEditingBatch({...editingBatch, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Course / कोर्स</Label>
                <select
                  value={editingBatch.course || ''}
                  onChange={(e) => setEditingBatch({...editingBatch, course: e.target.value})}
                  className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                  required
                >
                  <option value="">Select Course</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.name}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={editingBatch.start_date?.split('T')[0] || ''}
                    onChange={(e) => setEditingBatch({...editingBatch, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={editingBatch.end_date?.split('T')[0] || ''}
                    onChange={(e) => setEditingBatch({...editingBatch, end_date: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label>Timing</Label>
                <Input
                  value={editingBatch.timing || ''}
                  onChange={(e) => setEditingBatch({...editingBatch, timing: e.target.value})}
                  placeholder="10:00 AM - 12:00 PM"
                />
              </div>
              <div>
                <Label>Max Students</Label>
                <Input
                  type="number"
                  value={editingBatch.max_students || 30}
                  onChange={(e) => setEditingBatch({...editingBatch, max_students: parseInt(e.target.value) || 30})}
                  min="1"
                  max="100"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-to-r from-purple-600 to-blue-600">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Update Batch
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* View Students Dialog */}
      <Dialog open={viewStudentsOpen} onOpenChange={setViewStudentsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Students in {selectedBatch?.batch_name || selectedBatch?.name || 'Batch'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {loadingStudents ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              </div>
            ) : batchStudents.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {batchStudents.map((student) => (
                  <div key={student.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{student.name}</p>
                      <p className="text-sm text-slate-500 truncate">{student.email || student.mobile}</p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
                        onClick={() => handleOpenMoveStudent(student)}
                        title="Move to another batch"
                      >
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                        onClick={() => handleRemoveStudent(student.id)}
                        title="Remove from batch"
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No students in this batch yet</p>
                <Button 
                  className="mt-4 gap-2"
                  onClick={() => {
                    setViewStudentsOpen(false);
                    handleOpenAddStudents(selectedBatch);
                  }}
                >
                  <UserPlus className="w-4 h-4" />
                  Add Students
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Students to Batch Dialog */}
      <Dialog open={addStudentsOpen} onOpenChange={setAddStudentsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Add Students to {selectedBatch?.batch_name || selectedBatch?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-2 mb-4 p-3 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-700">
              <BookOpen className="w-4 h-4 inline mr-1" />
              Course: <strong>{selectedBatch?.course_name || selectedBatch?.course}</strong>
            </p>
            <p className="text-xs text-purple-600 mt-1">
              Only showing confirmed students enrolled in this course
            </p>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, phone, email..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <div className="mt-4">
            {loadingAvailable ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
              </div>
            ) : filteredAvailableStudents.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-500">
                    {filteredAvailableStudents.length} student(s) available
                  </p>
                  {selectedStudents.length > 0 && (
                    <span className="text-sm font-medium text-emerald-600">
                      {selectedStudents.length} selected
                    </span>
                  )}
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {filteredAvailableStudents.map((student) => (
                    <div 
                      key={student.id} 
                      onClick={() => toggleStudentSelection(student.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedStudents.includes(student.id)
                          ? 'bg-emerald-50 border-2 border-emerald-500'
                          : 'bg-slate-50 border-2 border-transparent hover:border-slate-200'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedStudents.includes(student.id)
                          ? 'bg-emerald-500 border-emerald-500'
                          : 'border-slate-300'
                      }`}>
                        {selectedStudents.includes(student.id) && (
                          <CheckCircle className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900">{student.name}</p>
                        <p className="text-sm text-slate-500 truncate">
                          {student.mobile} {student.email && `• ${student.email}`}
                        </p>
                      </div>
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                        {student.status || 'Confirmed'}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t mt-4">
                  <Button variant="outline" onClick={() => setAddStudentsOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                    onClick={handleAddStudentsToBatch}
                    disabled={selectedStudents.length === 0}
                  >
                    <UserPlus className="w-4 h-4" />
                    Add {selectedStudents.length > 0 ? `(${selectedStudents.length})` : ''} Students
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No available students for this course</p>
                <p className="text-sm text-slate-400 mt-1">
                  {studentSearch ? 'Try a different search term' : 'Add students with confirmed admission first'}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Student Dialog */}
      <Dialog open={moveStudentOpen} onOpenChange={setMoveStudentOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-blue-600" />
              Move Student to Another Batch
            </DialogTitle>
          </DialogHeader>
          
          {studentToMove && (
            <div className="mt-4 space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Student</p>
                <p className="font-medium text-slate-900">{studentToMove.name}</p>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-600">Current Batch</p>
                <p className="font-medium text-amber-800">{selectedBatch?.batch_name || selectedBatch?.name}</p>
              </div>
              
              <div>
                <Label>Select Target Batch / नई बैच चुनें</Label>
                <select
                  value={targetBatchId}
                  onChange={(e) => setTargetBatchId(e.target.value)}
                  className="w-full h-10 px-3 border rounded-lg bg-white text-sm mt-1"
                >
                  <option value="">Select Batch</option>
                  {batches
                    .filter(b => b.id !== selectedBatch?.id)
                    .map(batch => (
                      <option key={batch.id} value={batch.id}>
                        {batch.batch_name || batch.name} ({batch.course_name || batch.course})
                      </option>
                    ))
                  }
                </select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setMoveStudentOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                  onClick={handleMoveStudent}
                  disabled={!targetBatchId}
                >
                  <ArrowRight className="w-4 h-4" />
                  Move Student
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterBatchesPage;
